import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  IUsuario,
  IAuthResponse,
  ILoginCredentials,
  RolUsuario,
} from '../types/usuario.types';
import { authService } from '../services/auth.service';

interface AuthContextType {
  user: IUsuario | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: ILoginCredentials) => Promise<void>;
  logout: () => void;
  hasPermission: (roles: RolUsuario[]) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<IUsuario | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token'),
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      let storedToken = localStorage.getItem('token');
      let storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // Intentar cargar usuario del token almacenado y validar estructura mínima
          const parsedUser: IUsuario = JSON.parse(storedUser);

          // *VERIFICACIÓN CLAVE*: Asumimos que si el usuario existe, debería tener empresaId.
          // Si la respuesta de login (response.data.usuario) garantiza este campo, basta con verificar su presencia y tipo.
          if (!('empresaId' in parsedUser)) {
            console.warn(
              "Usuario cargado sin propiedad 'empresaId'. Se podría perder el contexto transaccional.",
            );
          }

          setUser(parsedUser);
        } catch (e) {
          console.error('Error al parsear usuario de localStorage:', e);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null); // Limpiar si el parsing falla
        }
      } else {
        setToken(null);
        setUser(null);
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const login = async (credentials: ILoginCredentials) => {
    try {
      const response: IAuthResponse = await authService.login(credentials);
      if (response.success) {
        setUser(response.data.usuario);
        setToken(response.data.token);
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.data.usuario));
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // Limpiar colores/tema del usuario para que el próximo usuario empiece limpio
    localStorage.removeItem('darkColors');
    localStorage.removeItem('lightColors');
    localStorage.removeItem('theme');
  };

  const hasPermission = (roles: RolUsuario[]) => {
    if (!user) return false;
    return roles.includes(user.rol as RolUsuario);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated: !!user && !!token,
        login,
        logout,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};
